import type { ImprovBrief, PerformerStatus } from '@stage/contracts';

/**
 * Pure helpers for the improv brief stage queue (§20.14). No I/O — the Performance Console feeds in
 * the polled list and these decide ordering and which brief is "active" on the large stage view.
 */

/**
 * Stage-queue priority. Accepted/queued briefs the performer has committed to come first, then fresh
 * submissions, then already-reviewed ones. Terminal states (performed/declined/skipped/hidden) are
 * excluded from the live stage queue entirely.
 */
const STATUS_RANK: Record<PerformerStatus, number> = {
  accepted: 0,
  queued: 1,
  shortlisted: 2,
  new: 3,
  reviewed: 4,
  performed: 90,
  skipped: 91,
  declined: 92,
  hidden: 99,
};

/** Statuses that keep a brief on the live stage queue. */
export function isQueueable(status: PerformerStatus): boolean {
  return STATUS_RANK[status] < 90;
}

/**
 * Order briefs for the stage queue: queueable only, by status priority, then oldest-submitted first
 * so the queue reads front-to-back in the order the performer would work through it.
 */
export function orderBriefs(briefs: readonly ImprovBrief[]): ImprovBrief[] {
  return briefs
    .filter((b) => isQueueable(b.status))
    .slice()
    .sort((a, b) => {
      const byStatus = STATUS_RANK[a.status] - STATUS_RANK[b.status];
      if (byStatus !== 0) return byStatus;
      return a.submittedAt.localeCompare(b.submittedAt);
    });
}

/**
 * Pick the active brief for the large stage view. Honours an explicit selection when it is still
 * queueable; otherwise falls back to the front of the ordered queue. Returns null when nothing is
 * performable.
 */
export function pickActiveBrief(
  briefs: readonly ImprovBrief[],
  currentId?: string | null,
): ImprovBrief | null {
  const ordered = orderBriefs(briefs);
  if (currentId) {
    const chosen = ordered.find((b) => b.briefId === currentId);
    if (chosen) return chosen;
  }
  return ordered[0] ?? null;
}
